import isEqual from 'lodash.isequal';
import { useSnackbar } from 'notistack';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { selectActiveOrganizationId } from '@/entities/session';
import { Monitoring } from '@/shared/lib';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import type { PresetViewType } from '../../api/types';
import { parseViewConfigParam } from '../../libs/parseViewConfigParam';
import {
  createPreset,
  deletePreset,
  setAsDefault,
  setupPresets,
  unsetAsDefault,
  updatePresetContent,
} from '../../model/slice';
import type { SavedPresetRecord } from '../../model/types';
import { usePresetsForViewType } from '../../model/usePresetsForViewType';
import { usePresetInitialization } from './usePresetInitialization';

interface ContextState {
  appliedPresetId: string;
  appliedPresetName?: string;
  presets: SavedPresetRecord[];
  applyPreset: (preset: SavedPresetRecord) => void;
  createPreset: (name: string) => Promise<void>;
  updatePreset: (id: string) => void;
  deletePreset: (id: string) => void;
  setAsDefault: (id: string) => void;
  unsetAsDefault: (id: string) => void;
}

const Context = createContext<ContextState | null>(null);
const NO_PRESSET_APPLIED = '';

interface Props<T> {
  children: ReactNode;
  metaLoaded: boolean;
  viewType: PresetViewType;
  snapshotValidator: (
    v: unknown,
  ) => { success: true; data: T } | { success: false; error: unknown };
  currentViewState: unknown;
  onApply: (preset: unknown) => void;
  onApplyVendorDefault: () => void;
  onApplyViewConfig?: (partial: unknown) => void;
}

export function SavedPresetsProvider<T>({
  children,
  metaLoaded,
  currentViewState,
  snapshotValidator,
  viewType,
  onApply,
  onApplyVendorDefault,
  onApplyViewConfig,
}: Props<T>) {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  const presets = usePresetsForViewType(viewType, snapshotValidator);
  const [appliedPresetId, setAppliedPresetId] = useState(NO_PRESSET_APPLIED);
  const navigateAndSetAppliedPreset = useCallback(
    (id: string) => {
      setAppliedPresetId(id);
      setSearchParams(
        {
          ...Object.fromEntries(searchParams),
          savedPreset: id,
        },
        { replace: true },
      );
    },
    [setSearchParams, searchParams],
  );
  const handleApplyVendorDefault = useCallback(() => {
    const { savedPreset, ...rest } = Object.fromEntries(searchParams);

    setSearchParams(rest, { replace: true });
    onApplyVendorDefault();
  }, [onApplyVendorDefault, searchParams, setSearchParams]);
  const viewConfigPartial = parseViewConfigParam(searchParams.get('viewConfig'));
  const handleApplyViewConfig = useCallback(
    (partial: unknown) => {
      const { viewConfig, ...rest } = Object.fromEntries(searchParams);

      setSearchParams(rest, { replace: true });
      onApplyViewConfig?.(partial);
    },
    [onApplyViewConfig, searchParams, setSearchParams],
  );
  const applyPreset = useCallback(
    (preset: SavedPresetRecord) => {
      if (preset.id === appliedPresetId) {
        return;
      }

      navigateAndSetAppliedPreset(preset.id);
      onApply(preset.preset);
    },
    [onApply, appliedPresetId, navigateAndSetAppliedPreset],
  );
  const appliedPreset = useMemo(() => {
    return presets.find((preset) => {
      return preset.id === appliedPresetId;
    });
  }, [presets, appliedPresetId]);

  useEffect(() => {
    dispatch(setupPresets(activeOrganizationId));
  }, [dispatch, activeOrganizationId]);

  usePresetInitialization({
    metaLoaded,
    applyPreset,
    applyVendorDefault: handleApplyVendorDefault,
    presets,
    viewConfigPartial: viewConfigPartial ?? undefined,
    applyViewConfig: onApplyViewConfig ? handleApplyViewConfig : undefined,
  });

  useEffect(() => {
    if (appliedPresetId === NO_PRESSET_APPLIED) {
      return;
    }

    const appliedPreset = presets.find((preset) => {
      return preset.id === appliedPresetId;
    });

    if (appliedPreset && !isEqual(appliedPreset.preset, currentViewState)) {
      const { savedPreset, ...rest } = Object.fromEntries(searchParams);

      setSearchParams(rest, { replace: true });
      setAppliedPresetId(NO_PRESSET_APPLIED);
    }
  }, [
    currentViewState,
    appliedPresetId,
    presets,
    searchParams,
    setSearchParams,
  ]);

  return (
    <Context.Provider
      value={{
        presets,
        appliedPresetId,
        appliedPresetName: appliedPreset?.name,
        applyPreset,
        updatePreset: (id) => {
          dispatch(updatePresetContent(id, currentViewState)).then(() => {
            const appliedPreset = presets.find((preset) => {
              return preset.id === id;
            })!;

            navigateAndSetAppliedPreset(appliedPreset.id);
            enqueueSnackbar(`"${appliedPreset.name}" successfully updated.`, {
              variant: 'success',
              autoHideDuration: 2000,
            });
          });
        },
        createPreset: (name) => {
          return dispatch(
            createPreset({
              viewType,
              name,
              preset: currentViewState,
            }),
          )
            .unwrap()
            .then((newPreset) => {
              setAppliedPresetId(newPreset.id);
              enqueueSnackbar(`"${name}" successfully saved.`, {
                variant: 'success',
                autoHideDuration: 2000,
              });
            })
            .catch((error) => {
              Monitoring.captureError(error);
              enqueueSnackbar('Unable to save preset. Please, try again.', {
                variant: 'error',
                autoHideDuration: 4000,
              });

              throw error;
            });
        },
        deletePreset: (id) => {
          dispatch(deletePreset(id))
            .unwrap()
            .then(() => {
              if (id === appliedPresetId) {
                setAppliedPresetId(NO_PRESSET_APPLIED);
              }
            })
            .catch(() => {
              enqueueSnackbar('Unable to delete preset. Please, try again.', {
                variant: 'error',
                autoHideDuration: 4000,
              });
            });
        },
        setAsDefault: (uuid) => {
          dispatch(setAsDefault(uuid)).catch(() => {
            enqueueSnackbar(
              'Unable to set preset as default. Please, try again.',
              {
                variant: 'error',
                autoHideDuration: 4000,
              },
            );
          });
        },
        unsetAsDefault: (uuid) => {
          dispatch(unsetAsDefault(uuid)).catch(() => {
            enqueueSnackbar(
              'Unable to unset preset as default. Please, try again.',
              {
                variant: 'error',
                autoHideDuration: 4000,
              },
            );
          });
        },
      }}
    >
      {children}
    </Context.Provider>
  );
}

export const useSavedPresetsContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error(
      'useSavedPresetsContext hook used outside <SavedPresetsProvider />',
    );
  }

  return context;
};
